import React, { useState, useEffect } from 'react';
import { X, Upload, Search, Check, Loader2, ShoppingCart, Users, Download } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@store/authStore';
import { useDataSourceStore } from '@store/dataSourceStore';
import { api } from '@services/api';
import toast from 'react-hot-toast';

interface ImportFromEshopModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  dataSourceId: string;
  targetListId?: string;
  lists: any[];
}

export const ImportFromEshopModal: React.FC<ImportFromEshopModalProps> = ({
  isOpen,
  onClose,
  companyId,
  dataSourceId,
  targetListId,
  lists,
}) => {
  const { accessToken } = useAuthStore();
  const { selectedDataSources } = useDataSourceStore();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<'select-eshop' | 'select-customers' | 'importing'>('select-eshop');
  const [selectedEshopId, setSelectedEshopId] = useState<string>('');
  const [selectedList, setSelectedList] = useState(targetListId || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState<Set<string>>(new Set());
  const [tablePage, setTablePage] = useState(1);
  const [subscribeAll, setSubscribeAll] = useState(true); // Default: subscribe all imported customers
  const customersPerPage = 100;

  // 🎯 Filters for customer selection (ALL FILTERS from customer table!)
  const [filterConsent, setFilterConsent] = useState<'all' | 'opted_in' | 'no_consent'>('all');
  const [filterGender, setFilterGender] = useState<'all' | 'male' | 'female'>('all');
  const [filterAge, setFilterAge] = useState<'all' | '18-30' | '31-40' | '41-50' | '51-60' | '61+'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterOrders, setFilterOrders] = useState<'all' | '0' | '1-5' | '6-10' | '11-20' | '21+'>('all');
  const [filterCustomerType, setFilterCustomerType] = useState<'all' | 'new' | 'returning' | 'vip' | 'at-risk'>('all');
  const [filterCartStatus, setFilterCartStatus] = useState<'all' | 'active' | 'abandoned' | 'converted' | 'none'>('all');
  const [filterConsultationStatus, setFilterConsultationStatus] = useState<'all' | 'abandoned' | 'completed' | 'none'>('all');
  const [filterEventType, setFilterEventType] = useState<'all' | 'signup' | 'login' | 'order' | 'cart_created' | 'cart_abandoned' | 'consultation_completed' | 'consultation_abandoned'>('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('all_time'); // 🔥 DEFAULT: All Time (not Last 60 days!)
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  // Handle modal close with state reset
  const handleClose = () => {
    // Reset all state
    setStep('select-eshop');
    setSelectedEshopId('');
    setSelectedCustomers(new Set());
    setSearchQuery('');
    setTablePage(1);
    setFilterConsent('all');
    setFilterGender('all');
    setFilterAge('all');
    setFilterStatus('all');
    // Call parent onClose
    onClose();
  };

  // Get e-shop data sources
  const eshopDataSources = selectedDataSources?.filter(
    (ds) => ds.availableModules?.includes('eshop') && ['postgresql', 'mysql', 'mongodb'].includes(ds.type)
  ) || [];

  // 📄 SERVER-SIDE PAGINATION: Fetch 100 customers per page
  const [serverPage, setServerPage] = React.useState(1);
  const CUSTOMERS_PER_PAGE = 100;

  const { data: customersData, isLoading: customersLoading, isFetching } = useQuery({
    queryKey: ['eshop-customers-paginated', selectedEshopId, serverPage, dateRangeFilter, filterOrders, filterCustomerType, filterCartStatus, filterConsultationStatus, filterEventType, customDateFrom, customDateTo],
    queryFn: async () => {
      if (!selectedEshopId) return { customers: [], total: 0 };

      console.log('📊 Fetching customers page', serverPage, 'from e-shop:', selectedEshopId);
      const response = await api.get(`/api/v1/eshops/${selectedEshopId}/customers`, {
        params: {
          companyId,
          limit: CUSTOMERS_PER_PAGE,
          page: serverPage,
          // 🔥 IMPORTANT: Pass ALL filters to backend (default to 'all' to get EVERYTHING!)
          filterConsent,
          filterGender,
          filterAge,
          filterStatus,
          filterOrders,
          filterCustomerType,
          filterCartStatus,
          filterConsultationStatus,
          filterEventType,
          dateRangeFilter,
          customDateFrom,
          customDateTo,
        },
      });
      const total = response.data.data.pagination?.total || response.data.data.total || 0;
      const customers = response.data.data.customers || [];
      console.log('✅ Loaded', customers.length, 'customers for preview (', total, 'total in DB)');
      return { customers, total }; // Return first 100 for preview - backend will query all for import
    },
    enabled: !!selectedEshopId && step === 'select-customers',
  });

  // 🚀 OPTIMIZED: Show first 100 customers for PREVIEW, backend handles full import
  const totalCustomers = customersData?.total || 0; // Total from backend
  const totalPages = Math.ceil(totalCustomers / CUSTOMERS_PER_PAGE);
  
  // 🎯 NEW: "Select All in Database" flag (selects ALL customers, not just current page)
  const [selectAllInDatabase, setSelectAllInDatabase] = React.useState(false);
  
  // Apply client-side filters to the preview (first 100 customers)
  const filteredCustomers = React.useMemo(() => {
    if (!customersData?.customers) return [];
    return customersData.customers.filter((customer: any) => {
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        const matchesSearch = 
          customer.email?.toLowerCase().includes(search) ||
          customer.first_name?.toLowerCase().includes(search) ||
          customer.last_name?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Consent filter
      if (filterConsent !== 'all') {
        const hasConsent = customer.marketing_consent === true;
        if (filterConsent === 'opted_in' && !hasConsent) return false;
        if (filterConsent === 'no_consent' && hasConsent) return false;
      }

      // Gender filter
      if (filterGender !== 'all') {
        const isMale = customer.gender === 0 || customer.sex === 0;
        const isFemale = customer.gender === 1 || customer.sex === 1;
        if (filterGender === 'male' && !isMale) return false;
        if (filterGender === 'female' && !isFemale) return false;
      }

      // Status filter
      if (filterStatus !== 'all') {
        const isActive = customer.is_active !== false; // Default to active if undefined
        if (filterStatus === 'active' && !isActive) return false;
        if (filterStatus === 'inactive' && isActive) return false;
      }

      // Age filter
      if (filterAge !== 'all') {
        const dob = customer.date_of_birth || customer.dob || customer.dateOfBirth;
        if (dob) {
          const age = Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
          
          if (filterAge === '18-30' && (age < 18 || age > 30)) return false;
          if (filterAge === '31-40' && (age < 31 || age > 40)) return false;
          if (filterAge === '41-50' && (age < 41 || age > 50)) return false;
          if (filterAge === '51-60' && (age < 51 || age > 60)) return false;
          if (filterAge === '61+' && age < 61) return false;
        }
      }

      // 🛍️ Orders filter
      if (filterOrders !== 'all') {
        const orders = customer.totalOrders || customer.total_orders || 0;
        if (filterOrders === '0' && orders !== 0) return false;
        if (filterOrders === '1-5' && (orders < 1 || orders > 5)) return false;
        if (filterOrders === '6-10' && (orders < 6 || orders > 10)) return false;
        if (filterOrders === '11-20' && (orders < 11 || orders > 20)) return false;
        if (filterOrders === '21+' && orders < 21) return false;
      }

      // 🎯 Customer Type filter
      if (filterCustomerType !== 'all') {
        const tier = (customer.customerTier || customer.customer_tier || '').toLowerCase();
        if (filterCustomerType === 'new' && tier !== 'new') return false;
        if (filterCustomerType === 'returning' && tier !== 'returning') return false;
        if (filterCustomerType === 'vip' && tier !== 'vip') return false;
        if (filterCustomerType === 'at-risk' && tier !== 'at risk') return false;
      }

      // 🛒 Cart Status filter
      if (filterCartStatus !== 'all') {
        const cartStatus = (customer.cartStatus || customer.cart_status || 'none').toLowerCase();
        if (filterCartStatus === 'active' && cartStatus !== 'active') return false;
        if (filterCartStatus === 'abandoned' && cartStatus !== 'abandoned') return false;
        if (filterCartStatus === 'converted' && cartStatus !== 'converted') return false;
        if (filterCartStatus === 'none' && cartStatus !== 'none') return false;
      }

      // 📋 Consultation Status filter
      if (filterConsultationStatus !== 'all') {
        const consultStatus = (customer.consultationStatus || customer.consultation_status || 'none').toLowerCase();
        if (filterConsultationStatus === 'abandoned' && consultStatus !== 'abandoned') return false;
        if (filterConsultationStatus === 'completed' && consultStatus !== 'completed') return false;
        if (filterConsultationStatus === 'none' && consultStatus !== 'none') return false;
      }

      // 📊 Event Type filter
      if (filterEventType !== 'all') {
        const eventType = (customer.lastEventType || customer.last_event_type || '').toLowerCase();
        if (filterEventType === 'signup' && eventType !== 'signup') return false;
        if (filterEventType === 'login' && eventType !== 'login') return false;
        if (filterEventType === 'order' && eventType !== 'order') return false;
        if (filterEventType === 'cart_created' && eventType !== 'cart_created') return false;
        if (filterEventType === 'cart_abandoned' && eventType !== 'cart_abandoned') return false;
        if (filterEventType === 'consultation_completed' && eventType !== 'consultation_completed') return false;
        if (filterEventType === 'consultation_abandoned' && eventType !== 'consultation_abandoned') return false;
      }

      // 📅 NOTE: Date filtering is handled by backend API now (not client-side)
      // The dateRangeFilter is passed to the backend API which filters at the DB level

      return true;
    });
  }, [customersData?.customers, searchQuery, filterConsent, filterGender, filterAge, filterStatus, filterOrders, filterCustomerType, filterCartStatus, filterConsultationStatus, filterEventType]);

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async () => {
      // 🎯 Check if "Select ALL in Database" is enabled
      if (selectAllInDatabase) {
        console.log('🚀 [IMPORT] Importing ALL customers from database using filters');
        console.log('📊 [IMPORT] Total customers:', totalCustomers);
        
        // Import ALL customers using filters
        const response = await api.post('/api/v1/getresponse/import/eshop', {
          companyId,
          dataSourceId,
          targetCampaignId: selectedList,
          eshopId: selectedEshopId,
          eshopDataSourceId: selectedEshopId,
          subscribeAll,
          filters: {
            eshopDataSourceId: selectedEshopId,
            dateRangeFilter,
            filterOrders,
            filterCustomerType,
            filterCartStatus,
            filterConsultationStatus,
            filterEventType,
            customDateFrom,
            customDateTo,
          },
        });
        return response.data;
      } else {
        // Import only selected customers
        console.log('🚀 [IMPORT] Importing selected customers:', selectedCustomers.size);
        
        if (selectedCustomers.size === 0) {
          toast.error('Please select at least one customer to import');
          throw new Error('No customers selected');
        }

        const selectedIds = Array.from(selectedCustomers);
        console.log('📋 [IMPORT] Selected customer IDs:', selectedIds);

        const response = await api.post('/api/v1/getresponse/import/eshop', {
          companyId,
          dataSourceId, // GetResponse data source
          eshopDataSourceId: selectedEshopId,
          targetCampaignId: selectedList, // Backend expects targetCampaignId
          customerIds: selectedIds, // Array of specific customer IDs
        });
        return response.data;
      }
    },
    onSuccess: (data) => {
      toast.success(`Successfully imported ${data?.data?.created || 0} new contacts and updated ${data?.data?.updated || 0} existing contacts!`);
      queryClient.invalidateQueries({ queryKey: ['getresponse-contacts'] });
      onClose();
    },
    onError: (error: any) => {
      console.error('Import error:', error);
      toast.error(error.response?.data?.message || 'Failed to import customers');
    },
  });

  const handleImport = () => {
    if (!selectedList) {
      toast.error('Please select a target list');
      return;
    }
    setStep('importing');
    importMutation.mutate();
  };

  // 🚫 OLD: Client-side pagination removed (now using server-side)
  // Display all customers on current page (already limited to 100 by server)
  const displayedCustomers = filteredCustomers;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Upload className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Import Customers from E-shop
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {step === 'select-eshop' && 'Step 1: Select e-shop and target list'}
                {step === 'select-customers' && 'Step 2: Select customers to import'}
                {step === 'importing' && 'Importing customers...'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={importMutation.isPending}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Select E-shop */}
          {step === 'select-eshop' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select E-shop <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedEshopId}
                  onChange={(e) => {
                    setSelectedEshopId(e.target.value);
                    setTablePage(1);
                    setSelectedCustomers(new Set());
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Choose an e-shop...</option>
                  {eshopDataSources.map((eshop) => (
                    <option key={eshop.id} value={eshop.id}>
                      {eshop.name} ({eshop.type})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Target List <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedList}
                  onChange={(e) => setSelectedList(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a list...</option>
                  {lists.map((list) => (
                    <option key={list.campaignId} value={list.campaignId}>
                      {list.name} ({list.contactCount || list.statistics?.totalContacts || 0} contacts)
                    </option>
                  ))}
                </select>
              </div>

              {/* Subscription Status for Bulk Import */}
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={subscribeAll}
                    onChange={(e) => setSubscribeAll(e.target.checked)}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Subscribe all imported customers to email campaigns
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        subscribeAll 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {subscribeAll ? 'All Subscribed' : 'All Unsubscribed'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {subscribeAll 
                        ? '✅ OVERRIDE MODE: All customers will be subscribed, ignoring individual marketing consent settings' 
                        : '⚠️ RESPECT CONSENT: Only customers with "Opted In" consent (green badge below) will be subscribed'}
                    </p>
                    {subscribeAll && (
                      <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-400">
                        <strong>⚠️ Warning:</strong> This will subscribe customers who previously opted out. Only use if you have explicit permission!
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {eshopDataSources.length === 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    ⚠️ No e-shops configured. Add an e-shop in Company Settings first.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Customers */}
          {step === 'select-customers' && (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by email or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* 🎯 Filters - ALL FILTERS FROM CUSTOMER TABLE! */}
              <div className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                  <select
                    value={filterConsent}
                    onChange={(e) => {
                      setFilterConsent(e.target.value as any);
                      setTablePage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all">All Consent</option>
                    <option value="opted_in">✓ Opted In</option>
                    <option value="no_consent">✗ No Consent</option>
                  </select>

                  <select
                    value={filterGender}
                    onChange={(e) => {
                      setFilterGender(e.target.value as any);
                      setTablePage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all">All Gender</option>
                    <option value="male">♂ Male</option>
                    <option value="female">♀ Female</option>
                  </select>

                  <select
                    value={filterAge}
                    onChange={(e) => {
                      setFilterAge(e.target.value as any);
                      setTablePage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all">All Ages</option>
                    <option value="18-30">18-30</option>
                    <option value="31-40">31-40</option>
                    <option value="41-50">41-50</option>
                    <option value="51-60">51-60</option>
                    <option value="61+">61+</option>
                  </select>

                  <select
                    value={filterStatus}
                    onChange={(e) => {
                      setFilterStatus(e.target.value as any);
                      setTablePage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="active">✓ Active</option>
                    <option value="inactive">✗ Inactive</option>
                  </select>

                  {/* 🎯 NEW: Orders Filter */}
                  <select
                    value={filterOrders}
                    onChange={(e) => {
                      setFilterOrders(e.target.value as any);
                      setTablePage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all">All Orders</option>
                    <option value="0">0 orders</option>
                    <option value="1-5">1-5 orders</option>
                    <option value="6-10">6-10 orders</option>
                    <option value="11-20">11-20 orders</option>
                    <option value="21+">21+ orders</option>
                  </select>

                  {/* 🎯 NEW: Customer Type Filter */}
                  <select
                    value={filterCustomerType}
                    onChange={(e) => {
                      setFilterCustomerType(e.target.value as any);
                      setTablePage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all">All Types</option>
                    <option value="new">🆕 New</option>
                    <option value="returning">🔄 Returning</option>
                    <option value="vip">⭐ VIP</option>
                    <option value="at-risk">⚠️ At Risk</option>
                  </select>

                  {/* 🎯 NEW: Cart Status Filter */}
                  <select
                    value={filterCartStatus}
                    onChange={(e) => {
                      setFilterCartStatus(e.target.value as any);
                      setTablePage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all">All Carts</option>
                    <option value="active">🛒 Active Cart</option>
                    <option value="abandoned">⚠️ Abandoned Cart</option>
                    <option value="converted">✅ Converted</option>
                    <option value="none">✗ No Cart</option>
                  </select>

                  {/* 🎯 NEW: Consultation Status Filter */}
                  <select
                    value={filterConsultationStatus}
                    onChange={(e) => {
                      setFilterConsultationStatus(e.target.value as any);
                      setTablePage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all">All Consultations</option>
                    <option value="abandoned">⚠️ Abandoned</option>
                    <option value="completed">✓ Completed</option>
                    <option value="none">✗ None</option>
                  </select>

                  {/* 🎯 NEW: Event Type Filter */}
                  <select
                    value={filterEventType}
                    onChange={(e) => {
                      setFilterEventType(e.target.value as any);
                      setTablePage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all">All Events</option>
                    <option value="signup">🆕 Signup</option>
                    <option value="login">🔐 Login</option>
                    <option value="order">🛍️ Order</option>
                    <option value="cart_created">🛒 Cart Created</option>
                    <option value="cart_abandoned">⚠️ Cart Abandoned</option>
                    <option value="consultation_completed">✅ Consultation</option>
                    <option value="consultation_abandoned">⏸️ Consultation</option>
                  </select>

                  {/* 🎯 NEW: Date Range Filter */}
                  <select
                    value={dateRangeFilter}
                    onChange={(e) => {
                      setDateRangeFilter(e.target.value);
                      setTablePage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all_time">🌍 All Time (ALL {totalCustomers.toLocaleString()} customers)</option>
                    <option value="today">Today</option>
                    <option value="last_7_days">Last 7 days</option>
                    <option value="last_14_days">Last 14 days</option>
                    <option value="last_30_days">Last 30 days</option>
                    <option value="last_60_days">Last 60 days</option>
                    <option value="last_90_days">Last 90 days</option>
                    <option value="custom">Custom Range</option>
                  </select>

                  {/* 🎯 Custom Date Inputs (if custom range selected) */}
                  {dateRangeFilter === 'custom' && (
                    <>
                      <input
                        type="date"
                        value={customDateFrom}
                        onChange={(e) => {
                          setCustomDateFrom(e.target.value);
                          setTablePage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                        placeholder="From"
                      />
                      <input
                        type="date"
                        value={customDateTo}
                        onChange={(e) => {
                          setCustomDateTo(e.target.value);
                          setTablePage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-sm"
                        placeholder="To"
                      />
                    </>
                  )}
                </div>

                {/* Filter Summary + Clear Button */}
                {(filterConsent !== 'all' || filterGender !== 'all' || filterAge !== 'all' || filterStatus !== 'all') && (
                  <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                      <span className="font-medium">
                        {filteredCustomers.length.toLocaleString()} of {totalCustomers.toLocaleString()} customers match filters
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setFilterConsent('all');
                        setFilterGender('all');
                        setFilterAge('all');
                        setFilterStatus('all');
                        setTablePage(1);
                      }}
                      className="px-3 py-1 bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-700 dark:text-blue-200 rounded text-sm font-medium transition-colors"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>

              {/* Debug Info - Customer Count */}
              {totalCustomers !== filteredCustomers.length && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    ℹ️ <strong>{totalCustomers.toLocaleString()}</strong> total customers in this e-shop
                    {' · '}
                    <strong>{filteredCustomers.length.toLocaleString()}</strong> match your filters
                    {totalCustomers !== filteredCustomers.length && filterConsent === 'all' && filterGender === 'all' && filterAge === 'all' && filterStatus === 'all' && (
                      <span className="text-blue-600 dark:text-blue-400">
                        {' · '}(Note: Only <strong>{customersData?.customers?.length || 0}</strong> loaded from server - may be API limit)
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Selection Info */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      {selectAllInDatabase 
                        ? `🎯 ALL ${totalCustomers.toLocaleString()} customers selected for import!`
                        : selectedCustomers.size > 0 
                        ? `${selectedCustomers.size} customer(s) selected on page ${serverPage}`
                        : `Page ${serverPage} of ${totalPages} (${filteredCustomers.length} on this page)`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* 🎯 Select All on Page */}
                    <button
                      onClick={() => {
                        if (selectedCustomers.size === filteredCustomers.length && !selectAllInDatabase) {
                          setSelectedCustomers(new Set());
                        } else {
                          setSelectAllInDatabase(false);
                          setSelectedCustomers(new Set(filteredCustomers.map((c: any) => c.customerId)));
                        }
                      }}
                      className="px-3 py-1 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 text-green-700 dark:text-green-200 rounded text-sm font-medium transition-colors"
                    >
                      {selectedCustomers.size === filteredCustomers.length && !selectAllInDatabase ? '☑️ Deselect Page' : `☐ Select Page (${filteredCustomers.length})`}
                    </button>
                    
                    {/* 🎯 Select ALL in Database */}
                    <button
                      onClick={() => {
                        setSelectAllInDatabase(!selectAllInDatabase);
                        if (!selectAllInDatabase) {
                          setSelectedCustomers(new Set()); // Clear specific selections
                        }
                      }}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        selectAllInDatabase
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-700 dark:text-blue-200'
                      }`}
                    >
                      {selectAllInDatabase ? `✅ ALL ${totalCustomers.toLocaleString()} Selected!` : `☐ Select ALL ${totalCustomers.toLocaleString()}`}
                    </button>
                    
                    {(selectedCustomers.size > 0 || selectAllInDatabase) && (
                      <button
                        onClick={() => {
                          setSelectedCustomers(new Set());
                          setSelectAllInDatabase(false);
                        }}
                        className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 font-medium"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">
                    {isFetching ? (
                      <span className="flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Loading all customers...
                      </span>
                    ) : (
                      `📊 Total: ${totalCustomers.toLocaleString()} | Page ${serverPage} of ${totalPages} (${CUSTOMERS_PER_PAGE} per page)`
                    )}
                  </span>
                </div>

                {/* Subscription Preview */}
                {!isFetching && filteredCustomers.length > 0 && (
                  <div className="pt-2 border-t border-green-200 dark:border-green-700">
                    {subscribeAll ? (
                      <div className="flex items-center gap-2 text-xs">
                        <div className="flex items-center gap-1 text-green-700 dark:text-green-400">
                          <span className="font-semibold">✓ {filteredCustomers.length} will be SUBSCRIBED</span>
                          <span className="text-gray-500">(override mode)</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 text-xs">
                        {(() => {
                          const withConsent = filteredCustomers.filter((c: any) => c.marketing_consent || c.subscribed).length;
                          const withoutConsent = filteredCustomers.length - withConsent;
                          return (
                            <>
                              <div className="flex items-center gap-1 text-green-700 dark:text-green-400">
                                <span className="font-semibold">✓ {withConsent} will be SUBSCRIBED</span>
                                <span className="text-gray-500">(have consent)</span>
                              </div>
                              <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                                <span className="font-semibold">✗ {withoutConsent} will be UNSUBSCRIBED</span>
                                <span className="text-gray-500">(no consent)</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Customers Table */}
              {customersLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                </div>
              ) : (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={displayedCustomers.length > 0 && displayedCustomers.every((c: any) => selectedCustomers.has(c.email))}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Select all on current page
                                  const newSelected = new Set(selectedCustomers);
                                  displayedCustomers.forEach((c: any) => newSelected.add(c.customerId));
                                  setSelectedCustomers(newSelected);
                                } else {
                                  // Deselect all on current page
                                  const newSelected = new Set(selectedCustomers);
                                  displayedCustomers.forEach((c: any) => newSelected.delete(c.customerId));
                                  setSelectedCustomers(newSelected);
                                }
                              }}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                              title="Select all on this page"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Phone</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Orders</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cart</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Consultation</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tier</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Event</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Consent</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {displayedCustomers.length === 0 ? (
                          <tr>
                            <td colSpan={10} className="px-6 py-12 text-center">
                              <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                              <p className="text-gray-600 dark:text-gray-400">
                                {isFetching ? 'Loading customers...' : 'No customers found'}
                              </p>
                            </td>
                          </tr>
                        ) : (
                          displayedCustomers.map((customer: any) => (
                            <tr
                              key={customer.email}
                              className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${
                                selectedCustomers.has(customer.email) ? 'bg-green-50 dark:bg-green-900/20' : ''
                              }`}
                              onClick={() => {
                                const newSelected = new Set(selectedCustomers);
                                if (newSelected.has(customer.customerId)) {
                                  newSelected.delete(customer.customerId);
                                } else {
                                  newSelected.add(customer.customerId);
                                }
                                setSelectedCustomers(newSelected);
                              }}
                            >
                              <td className="px-4 py-4">
                                <input
                                  type="checkbox"
                                  checked={selectedCustomers.has(customer.customerId)}
                                  onChange={() => {}}
                                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                              </td>
                              <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                                {customer.email}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                {customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || '-'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                {customer.phone || customer.phoneNumber || customer.phone_number || customer.telephone || customer.mobile || '-'}
                              </td>
                              {/* 🛍️ ORDERS Column */}
                              <td className="px-6 py-4 text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900 dark:text-white">
                                    {customer.totalOrders || customer.total_orders || 0}
                                  </span>
                                  {(customer.totalSpent || customer.total_spent) > 0 && (
                                    <span className="text-xs text-green-600 dark:text-green-400">
                                      £{(customer.totalSpent || customer.total_spent || 0).toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </td>
                              {/* 🛒 CART Column */}
                              <td className="px-6 py-4 text-sm">
                                {customer.cartStatus && customer.cartStatus !== 'none' ? (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    customer.cartStatus === 'abandoned' 
                                      ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                      : customer.cartStatus === 'active'
                                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  }`}>
                                    {customer.cartStatus}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </td>
                              {/* 📋 CONSULTATION Column */}
                              <td className="px-6 py-4 text-sm">
                                {customer.consultationStatus && customer.consultationStatus !== 'none' ? (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    customer.consultationStatus === 'completed'
                                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                  }`}>
                                    {customer.consultationStatus}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </td>
                              {/* 🎯 CUSTOMER TIER Column */}
                              <td className="px-6 py-4 text-sm">
                                {customer.customerTier ? (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    customer.customerTier === 'VIP'
                                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                      : customer.customerTier === 'At Risk'
                                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                      : customer.customerTier === 'Returning'
                                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  }`}>
                                    {customer.customerTier}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">New</span>
                                )}
                              </td>
                              {/* 📊 LAST EVENT Column */}
                              <td className="px-6 py-4 text-sm">
                                {customer.lastEventType ? (
                                  <span className="text-xs text-gray-600 dark:text-gray-400">
                                    {customer.lastEventType === 'order' && '🛍️ Order'}
                                    {customer.lastEventType === 'cart_abandoned' && '⚠️ Cart'}
                                    {customer.lastEventType === 'login' && '🔐 Login'}
                                    {customer.lastEventType === 'signup' && '🆕 Signup'}
                                    {customer.lastEventType === 'consultation_completed' && '✅ Consult'}
                                    {customer.lastEventType === 'consultation_abandoned' && '⏸️ Consult'}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </td>
                              {/* ✅ CONSENT Column */}
                              <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                {(() => {
                                  const hasConsent = customer.marketing_consent ?? customer.subscribed ?? false;
                                  return (
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      hasConsent 
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                    }`}>
                                      {hasConsent ? '✓ Opted In' : '✗ No Consent'}
                                    </span>
                                  );
                                })()}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* 📄 SERVER-SIDE PAGINATION Controls */}
                  {totalPages > 1 && (
                    <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing page {serverPage} of {totalPages} ({filteredCustomers.length} customers on this page)
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setServerPage(1)}
                          disabled={serverPage === 1 || customersLoading}
                          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          First
                        </button>
                        <button
                          onClick={() => setServerPage(prev => Math.max(1, prev - 1))}
                          disabled={serverPage === 1 || customersLoading}
                          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          ← Previous
                        </button>
                        <span className="text-sm text-gray-700 dark:text-gray-300 px-3 font-semibold">
                          Page {serverPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setServerPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={serverPage === totalPages || customersLoading}
                          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Next →
                        </button>
                        <button
                          onClick={() => setServerPage(totalPages)}
                          disabled={serverPage === totalPages || customersLoading}
                          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Importing */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="w-16 h-16 animate-spin text-green-600 mb-4" />
              <p className="text-lg font-medium text-gray-900 dark:text-white">
                Importing {selectedCustomers.size || filteredCustomers.length} customer(s)...
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Please wait, this may take a moment
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          {step === 'select-eshop' && (
            <>
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep('select-customers')}
                disabled={!selectedEshopId || !selectedList}
                className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next: Select Customers
              </button>
            </>
          )}

          {step === 'select-customers' && (
            <>
              <button
                onClick={() => {
                  setStep('select-eshop');
                  setTablePage(1);
                  setSelectedCustomers(new Set());
                }}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={customersLoading || (!selectAllInDatabase && selectedCustomers.size === 0)}
                className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" />
                {selectAllInDatabase 
                  ? `Import ALL ${totalCustomers.toLocaleString()} Customers` 
                  : selectedCustomers.size > 0 
                  ? `Import ${selectedCustomers.size} Selected`
                  : 'Select Customers to Import'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
